import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {cn} from '@/lib/utils'
import type {DecisionLocal, TarjetaCola} from '@/lib/bateria-cola'

/** Un solo mensaje: el genérico del API («recargue la página») contradice al específico con decisiones sin sincronizar. */
export function AvisoSincronizacion({
  porSincronizar,
  reintentoAgotado,
  reintentoEn,
  onReintentar,
}: {
  porSincronizar: number
  reintentoAgotado: boolean
  reintentoEn: number | null
  onReintentar: () => void
}) {
  const {t} = useTranslation()
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-2 rounded-lg px-4 py-2.5 text-sm shadow-pop',
        reintentoAgotado ? 'notice-red' : 'notice-amber'
      )}
      data-testid="cola-sin-sincronizar">
      <span>
        {reintentoAgotado
          ? t('admin.bateria.cola.sincronizacion.agotado', {count: porSincronizar})
          : reintentoEn !== null
            ? t('admin.bateria.cola.sincronizacion.reintentando', {
                count: porSincronizar,
                segundos: Math.max(reintentoEn, 0),
              })
            : t('admin.bateria.cola.sincronizacion.pendientes', {count: porSincronizar})}
      </span>
      <Button size="sm" variant="outline" data-testid="cola-reintentar" onClick={onReintentar}>
        {t('admin.bateria.cola.sincronizacion.reintentar')}
      </Button>
    </div>
  )
}

export function AvisoRecuperando({cuantas}: {cuantas: number}) {
  const {t} = useTranslation()
  return (
    <p className="px-1 py-1 font-mono text-[11px] text-muted-foreground" data-testid="cola-recuperando">
      {t('admin.bateria.cola.sincronizacion.recuperando', {count: cuantas})}
    </p>
  )
}

export function AvisoConflictos({total, onVerConflictos}: {total: number; onVerConflictos: () => void}) {
  const {t} = useTranslation()
  return (
    <div
      className="notice-amber flex flex-wrap items-center justify-between gap-2 rounded-lg px-4 py-3 text-sm"
      data-testid="cola-conflictos">
      <span>{t('admin.bateria.cola.conflicto', {count: total})}</span>
      <Button size="sm" variant="outline" data-testid="cola-ver-conflictos" onClick={onVerConflictos}>
        {t('admin.bateria.cola.verConflictos')}
      </Button>
    </div>
  )
}

export function AvisoConflictoActual({
  conflicto,
  tarjeta,
  onDejar,
  onCambiar,
}: {
  conflicto: DecisionLocal
  tarjeta: TarjetaCola
  onDejar: () => void
  onCambiar: () => void
}) {
  const {t} = useTranslation()
  // Un `ubicar` en conflicto nunca trae valor ganador (`_aplicar_ubicar` manda `None`) y su autor suele ser
  // el sistema: usa el copy del 409 de `descartar_hoja` (`KNOWN_ERRORS`).
  if (tarjeta.tipo === 'ubicar') {
    return (
      <div
        className="notice-amber flex flex-wrap items-center justify-between gap-2 rounded-lg px-4 py-3 text-sm"
        data-testid="cola-conflicto-actual">
        <span>{t('admin.bateria.cola.ubicar.errors.yaResuelta')}</span>
        <Button size="sm" variant="outline" data-testid="cola-conflicto-dejar" onClick={onDejar}>
          {t('admin.bateria.cola.conflictoDejar')}
        </Button>
      </div>
    )
  }
  const valor = conflicto.ganador
    ? t(`admin.bateria.escala.${conflicto.ganador}`, {defaultValue: conflicto.ganador})
    : t('admin.bateria.cola.conflictoSinValor')
  return (
    <div
      className="notice-amber flex flex-wrap items-center justify-between gap-2 rounded-lg px-4 py-3 text-sm"
      data-testid="cola-conflicto-actual">
      <span>
        {conflicto.decididoPorNombre
          ? t('admin.bateria.cola.conflictoTarjetaDe', {valor, nombre: conflicto.decididoPorNombre})
          : t('admin.bateria.cola.conflictoTarjeta', {valor})}
      </span>
      <span className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" data-testid="cola-conflicto-dejar" onClick={onDejar}>
          {t('admin.bateria.cola.conflictoDejar')}
        </Button>
        <Button size="sm" data-testid="cola-conflicto-forzar" onClick={onCambiar}>
          {t('admin.bateria.cola.conflictoCambiar')}
        </Button>
      </span>
    </div>
  )
}

export function AvisoCargandoTramos({cargadas, total}: {cargadas: number; total: number}) {
  const {t} = useTranslation()
  return (
    <p className="px-1 font-mono text-[11px] text-muted-foreground" data-testid="cola-cargando-tramos">
      {t('admin.bateria.cola.cargandoTramos', {cargadas, total})}
    </p>
  )
}

export function AvisoErrorCarga({onReintentar}: {onReintentar: () => void}) {
  const {t} = useTranslation()
  return (
    <div className="notice-red space-y-3 rounded-lg px-5 py-6 text-sm" data-testid="cola-error-carga">
      <p>{t('admin.bateria.cola.error.primerTramo')}</p>
      <Button size="sm" variant="outline" data-testid="cola-error-carga-reintentar" onClick={onReintentar}>
        {t('admin.bateria.cola.error.reintentar')}
      </Button>
    </div>
  )
}

export function AvisoErrorTramos({onReintentar}: {onReintentar: () => void}) {
  const {t} = useTranslation()
  return (
    <div
      className="notice-red flex flex-wrap items-center justify-between gap-2 rounded-lg px-4 py-3 text-sm"
      data-testid="cola-error-tramos">
      <span>{t('admin.bateria.cola.error.tramos')}</span>
      <Button size="sm" variant="outline" data-testid="cola-error-tramos-reintentar" onClick={onReintentar}>
        {t('admin.bateria.cola.error.reintentar')}
      </Button>
    </div>
  )
}

export function AvisoDescarteHecho({
  onDeshacer,
  pending,
  error,
}: {
  onDeshacer: () => void
  pending: boolean
  error: string | null
}) {
  const {t} = useTranslation()
  return (
    <div
      className="notice-amber flex flex-wrap items-center justify-between gap-2 rounded-lg px-4 py-3 text-sm"
      data-testid="cuarentena-descartada-aviso">
      <span>
        {t('admin.bateria.cuarentena.descartadaAviso')}
        {error ? ` · ${error}` : ''}
      </span>
      <Button
        size="sm"
        variant="outline"
        data-testid="cuarentena-deshacer-descarte"
        disabled={pending}
        onClick={onDeshacer}>
        {t('admin.bateria.cuarentena.deshacerDescarte')}
      </Button>
    </div>
  )
}

/** La cola llegó al techo de 10.000 tarjetas con el cursor vivo; lo siguiente se siembra aparte, nunca bajo el cursor. */
export function AvisoColaRecortada({cargadas, onTraerSiguientes}: {cargadas: number; onTraerSiguientes: () => void}) {
  const {t} = useTranslation()
  return (
    <div
      className="notice-amber flex flex-wrap items-center justify-between gap-2 rounded-lg px-4 py-3 text-sm"
      data-testid="cola-recortada">
      <span>{t('admin.bateria.cola.recortada.aviso', {count: cargadas})}</span>
      <Button size="sm" variant="outline" data-testid="cola-traer-siguientes" onClick={onTraerSiguientes}>
        {t('admin.bateria.cola.recortada.traer')}
      </Button>
    </div>
  )
}

/** Un filtro dejó la vista en cero: no es el lote agotado, así que nunca dice «Revisión terminada». */
export function AvisoFiltroVacio({pct, onQuitar}: {pct: number | null; onQuitar: () => void}) {
  const {t} = useTranslation()
  return (
    <div className="surface flex flex-col items-start gap-3 px-5 py-6 text-sm" data-testid="cola-filtro-vacio">
      <p className="text-muted-foreground">
        {pct !== null
          ? t('admin.bateria.cola.filtros.vacio.confianza', {pct})
          : t('admin.bateria.cola.filtros.vacio.generico')}
      </p>
      <Button size="sm" variant="outline" data-testid="cola-filtro-vacio-quitar" onClick={onQuitar}>
        {t('admin.bateria.cola.filtros.vacio.quitar')}
      </Button>
    </div>
  )
}
